import {
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  Text,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import RichTextEditor from "react-rte";
import { formatToReadableTime } from "../../../utils/utils";
import { sortByLastName } from "../../Assessment/Rater/utils";
import { Card } from "../../Card";
import { MULTI, Selector } from "../../Selectors/Selector";
import { TermSelect } from "../../TermManagement/Terms/TermSelect";
import { CourseReviewContext } from "../CourseReview";

const FacultyReflections = () => {
  const { t } = useTranslation();
  const { reflections, draftData, setDraftData } =
    useContext(CourseReviewContext);
  const location = useLocation();
  const { relevantTerms, stats = {} } = location.state;
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [filteredReflections, setFilteredReflections] = useState([]);

  const sortedAuthors = Array.from(
    new Set(reflections.map((r) => r.metadata.author))
  )
    .map((author) => ({ label: author, id: author, value: author }))
    .sort(sortByLastName);

  useEffect(() => {
    let filteredReflections = reflections.filter((ref) =>
      relevantTerms.find((t) => t.id === ref.term_id)
    );
    if (selectedTerms.length > 0) {
      filteredReflections = filteredReflections.filter((ref) =>
        selectedTerms.find((term) => term === ref.term_id)
      );
    }
    if (selectedAuthors.length > 0) {
      filteredReflections = filteredReflections.filter((ref) =>
        selectedAuthors.find((author) => author === ref.metadata.author)
      );
    }
    setFilteredReflections(filteredReflections);
  }, [reflections, selectedTerms, selectedAuthors, relevantTerms]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Heading level="h3" as="h2">
          {t("courseReview.facultyReflections")}
        </Heading>
        {stats.section_count > 0 && (
          <Text>
            {t("courseReview.sectionsWithReflections", {
              num: stats.published_reflections_count,
              total: stats.section_count,
            })}
          </Text>
        )}
      </div>
      <View
        as="div"
        borderWidth="0 0 small 0"
        width="100%"
        margin="xxx-small 0 small 0"
      />
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <div style={{ width: "50%" }}>
          <TermSelect
            id="faculty-reflection-term-select"
            label={t("terms")}
            selectHandler={(terms) => setSelectedTerms(terms)}
            selectedTerms={selectedTerms}
            size="small"
            filteredTermIds={relevantTerms.map((t) => t.id)}
          />
        </div>
        <div style={{ width: "50%" }}>
          <Selector
            id="faculty-reflection-author-select"
            type={MULTI}
            label={t("courseReview.authors")}
            selectHandler={(authors) => setSelectedAuthors(authors)}
            selectedOptions={selectedAuthors}
            size="small"
            options={sortedAuthors}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: "0.75rem",
          margin: "-0.75rem",
          overflowY: "auto",
        }}
      >
        {filteredReflections.length === 0 ? (
          <Text>{t("reflections.noReflections")}</Text>
        ) : (
          filteredReflections.map((ref, key) => (
            <Card
              key={key}
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                height: "auto",
              }}
            >
              <Flex
                alignItems="start"
                gap="small"
                margin="0 0 small 0"
                wrap="wrap"
              >
                <Flex.Item shouldShrink shouldGrow>
                  <Text as="p" fontStyle="italic" size="small">
                    {ref.metadata.author}
                    {" - "}
                    {formatToReadableTime(new Date(ref.timestamp))}
                  </Text>
                </Flex.Item>
                <Flex.Item align="end">
                  <IconButton
                    screenReaderLabel={t("courseReview.addReflection")}
                    disabled={draftData.selectedReflections.find(
                      (r) => r.id.split("=")[0] === ref.id.split("=")[0]
                    )}
                    onClick={() => {
                      setDraftData((prev) => ({
                        ...prev,
                        selectedReflections: [ref, ...prev.selectedReflections],
                      }));
                    }}
                    renderIcon={IconAddLine}
                    color="success"
                    size="small"
                  />
                </Flex.Item>
              </Flex>
              {ref.from_template ? (
                <Flex wrap="wrap" gap="medium">
                  {ref.sections.map((section) => (
                    <Flex.Item key={section.id} width="100%">
                      <Flex wrap="wrap" gap="small">
                        <Flex.Item width="100%">
                          <Text weight="bold">{section.header_question}</Text>
                        </Flex.Item>
                        <Flex.Item>
                          <div
                            style={{
                              borderLeft: "2px solid #03893D",
                              paddingLeft: "0.75rem",
                            }}
                          >
                            <RichTextEditor
                              className="readOnlyClass"
                              value={RichTextEditor.createValueFromString(
                                section.content,
                                "html"
                              )}
                              readOnly
                            />
                          </div>
                        </Flex.Item>
                      </Flex>
                    </Flex.Item>
                  ))}
                </Flex>
              ) : (
                <RichTextEditor
                  className="readOnlyClass"
                  value={RichTextEditor.createValueFromString(
                    ref.content,
                    "html"
                  )}
                  readOnly
                />
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default FacultyReflections;
